/**
 * Timecard newsletter: when the modal may open, and what a submission is allowed to claim.
 *
 * Two jobs, and both are mostly about refusing.
 *
 * The modal opens by itself only when nothing else is happening. The list of contexts it declines
 * in is long and deliberately so — an uninvited dialog that steals focus from a search field, a
 * cart drawer or a cookie banner is worse than never appearing at all, and every one of those is a
 * thing a visitor started and this did not.
 *
 * A submission reports what the server said. Shopify's customer form is the source of truth: the
 * success panel shown after an enhanced submit is lifted out of the server's own response, not
 * built here, so the wording a visitor sees is the wording Liquid rendered under the store's real
 * configuration. A resolved fetch is not a success. A 200 is not a success. Only a response that
 * actually carries Shopify's acceptance is.
 *
 * ## Progressive enhancement, and where it stops
 *
 * The form works with this file absent: it is a real `{% form 'customer' %}` that posts, reloads
 * and renders its state from Liquid. The enhancement replaces that reload with a fetch so the
 * modal can transform in place.
 *
 * It stands down completely when Shopify's captcha is present. A captcha token is issued to a real
 * form submission, and intercepting one is how an enhanced form starts failing for exactly the
 * visitors the captcha was protecting against. Native submit, page reload, server-rendered success.
 * Same outcome, fewer moving parts.
 */

const STORAGE = {
  dismissed: 'timecard:newsletter:dismissed-at',
  done: 'timecard:newsletter:done',
  elapsed: 'timecard:newsletter:elapsed',
  pending: 'timecard:newsletter:submitted-from',
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** How long a pending submission may run before the form is handed back, retryable. */
const SUBMIT_TIMEOUT_MS = 15000;

/**
 * How long an operation must run before it is worth saying so.
 *
 * Under 350ms a spinner is a flash: it appears and vanishes inside one blink, which reads as a
 * glitch rather than as progress. Nothing is delayed to reach this threshold — fast stays fast,
 * and the indicator simply never appears.
 */
const SPINNER_DELAY_MS = 350;

/**
 * Storage that tells the truth about being unavailable instead of throwing into the page.
 *
 * The area is looked up by NAME and inside the try, not passed in as an object. Reading
 * `window.localStorage` is itself a throwing operation when storage is blocked — some browsers
 * raise a SecurityError on the property access, before any method is called — so a helper that
 * takes `window.localStorage` as an argument has already thrown at its call site by the time its
 * own try block could have helped.
 */
const store = {
  /** @param {'local' | 'session'} name */
  area(name) {
    return name === 'session' ? window.sessionStorage : window.localStorage;
  },
  /** @param {'local' | 'session'} name @param {string} key */
  read(name, key) {
    try {
      return this.area(name).getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {'local' | 'session'} name @param {string} key @param {string} value */
  write(name, key, value) {
    try {
      this.area(name).setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  /** @param {'local' | 'session'} name @param {string} key */
  remove(name, key) {
    try {
      this.area(name).removeItem(key);
    } catch {
      /* Nothing to do: the value we wanted gone was never storable in the first place. */
    }
  },
  /** Whether persistence works at all. Private browsing and blocked storage both land here. */
  get available() {
    try {
      const probe = '__timecard_probe__';
      const area = this.area('local');
      area.setItem(probe, '1');
      area.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  },
};

/** A signup Shopify confirmed, remembered so the launcher stops asking. */
function hasSignedUp() {
  const memberModal = document.querySelector('[data-member-mode]');
  if (memberModal) return memberModal.dataset.member === 'true';
  return store.read('local', STORAGE.done) === '1';
}

/** Whether a dismissal is still inside its quiet period. */
function isDismissed(days) {
  const at = Number(store.read('local', STORAGE.dismissed));
  if (!Number.isFinite(at) || at <= 0) return false;
  return Date.now() - at < days * DAY_MS;
}

/**
 * Contexts an uninvited dialog must not appear in.
 *
 * Each of these is something the visitor is in the middle of. Opening over any of them takes focus
 * away from work they started, and in the cookie banner's case it would cover a consent decision
 * with a marketing request — which is the one overlap that is not merely rude.
 */
function somethingElseIsHappening() {
  if (document.visibilityState !== 'visible') return true;

  // Another dialog, including this one. Stacked modals are how focus gets lost.
  if (document.querySelector('dialog[open]')) return true;

  // Horizon's drawers and menus are <details>, and its scroll-locking ones say so.
  if (document.querySelector('details[open][scroll-lock]')) return true;
  if (document.querySelector('#header-component details[open]')) return true;
  if (document.querySelector('.menu-drawer[open], cart-drawer-component[open]')) return true;

  // Shopify's own consent banner and privacy prompts.
  if (document.querySelector('#shopify-pc__banner, #shopify-pc__prefs')) return true;

  // Focus is somewhere that would be stolen: a field being filled, or any open popover.
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    if (active.isContentEditable) return true;
    if (active.closest('input, textarea, select, [role="search"], [popover]:popover-open')) return true;
  }

  return false;
}

/** The single modal on the page, if it rendered. */
function getModal() {
  return document.querySelector('[data-timecard-newsletter-modal]');
}

/**
 * Resolves once the modal can actually be opened.
 *
 * `showDialog` is a method on Horizon's `dialog-component`, which only exists after that custom
 * element has been defined and the element upgraded. This file and `dialog.js` are separate module
 * scripts, so the ordering holds in the ordinary case — but it is ordering, not a guarantee, and
 * when it does not hold every caller silently fails: `openModal` returns false, the launcher click
 * does nothing at all, and the visitor is left clicking a button that appears broken.
 *
 * Waiting on the registry instead of on script order removes the assumption. `upgrade` covers the
 * case where the element was parsed before the definition landed.
 *
 * @param {Element} modal
 * @returns {Promise<void>}
 */
function whenDialogReady(modal) {
  if (typeof (/** @type {any} */ (modal).showDialog) === 'function') return Promise.resolve();

  const name = modal.localName;
  if (!name.includes('-') || !window.customElements) return Promise.resolve();

  return customElements.whenDefined(name).then(() => {
    customElements.upgrade(modal);
  });
}

/**
 * Opens the modal, refusing if anything has changed since eligibility was decided.
 *
 * The check is repeated here rather than trusted from the caller because the timer that leads to
 * an auto-open runs for fifteen seconds, and a visitor can open a cart drawer in fourteen of them.
 *
 * @param {Element | null} modal
 * @param {boolean} guarded - Whether to refuse on a busy context. A click is never refused.
 */
function openModal(modal, guarded) {
  if (!modal) return false;
  if (guarded && somethingElseIsHappening()) return false;

  const component = /** @type {any} */ (modal);
  if (typeof component.showDialog !== 'function') return false;

  component.showDialog();
  return true;
}

/* ─────────────────────────────────────────────────────────────── the launcher ───── */

/**
 * The launcher ships hidden and is revealed only once this file has decided it should exist.
 *
 * That order matters: markup that is visible by default and hidden by script flashes on every page
 * for everyone it does not apply to, including the visitor who already signed up.
 */
function syncLauncher() {
  const launcher = document.querySelector('[data-timecard-newsletter-launcher]');
  if (!(launcher instanceof HTMLElement)) return;

  const banner = document.querySelector('#shopify-pc__banner, #shopify-pc__prefs');
  const shouldShow = !hasSignedUp() && !banner && Boolean(getModal());

  launcher.hidden = !shouldShow;
}

/* ─────────────────────────────────────────────────────────────── submission ───── */

/**
 * Marks a form busy, with the indicator deferred past the flash threshold.
 *
 * @param {HTMLFormElement} form
 * @param {boolean} busy
 * @returns {number | undefined} The pending reveal timer, so the caller can cancel it.
 */
function setBusy(form, busy) {
  const submit = form.querySelector('[data-timecard-newsletter-submit]');
  if (!(submit instanceof HTMLButtonElement)) return undefined;

  if (!busy) {
    submit.disabled = false;
    submit.removeAttribute('aria-busy');
    form.removeAttribute('data-timecard-pending');
    return undefined;
  }

  // Disabled immediately — duplicate submissions are prevented at the first click, not at 350ms.
  submit.disabled = true;
  submit.setAttribute('aria-busy', 'true');

  return window.setTimeout(() => {
    form.setAttribute('data-timecard-pending', 'true');
  }, SPINNER_DELAY_MS);
}

/** How long a native submission may leave a button disabled before the form is handed back. */
const NATIVE_BUSY_CEILING_MS = 12000;

/**
 * Busy state for the native path, which this file does not control the end of.
 *
 * On the captcha path the browser owns the submission: there is no promise to await and no
 * response to read, and the page is expected to navigate away. But a captcha can be cancelled,
 * rejected, or simply closed without ever submitting — and a back-navigation can restore this
 * exact page from bfcache with the button still disabled. Either way the visitor is left looking
 * at a control that cannot be pressed and never explains itself.
 *
 * So the busy state is marked on a timeout — after the browser has taken the submission, because
 * disabling a submit button synchronously inside its own submit event is how a form quietly stops
 * submitting — and is then released by whichever comes first: a ceiling, or the page coming back.
 *
 * Nothing here touches the captcha. It is not intercepted, no handler is removed, and no token is
 * bypassed; only the button's appearance is managed.
 *
 * @param {HTMLFormElement} form
 */
function markBusyRecoverably(form) {
  window.setTimeout(() => {
    const reveal = setBusy(form, true);

    const release = () => {
      if (reveal) window.clearTimeout(reveal);
      setBusy(form, false);
      window.removeEventListener('pageshow', release);
    };

    window.setTimeout(release, NATIVE_BUSY_CEILING_MS);
    window.addEventListener('pageshow', release);
  }, 0);
}

/**
 * Reads Shopify's answer out of a response to a customer-form post.
 *
 * Success is the redirect Shopify performs on acceptance, which lands on the referring page with
 * `customer_posted=true`. The server-rendered success panel in the returned document is the second
 * witness, and the one the visitor is actually shown.
 *
 * Anything this cannot read confidently returns `unknown`, and an unknown answer is never treated
 * as either outcome — the caller hands the submission back to the browser instead.
 *
 * The parser is a parameter so this decision can be tested on its own. Only the default is used in
 * the browser; the seam exists because the rule being tested is which combination of signals counts
 * as a subscription, and that rule should not need a DOM to state.
 *
 * @param {{ url: string }} response
 * @param {string} html
 * @param {string} doneId - The success panel belonging to the form that was submitted.
 * @param {string} errorId - That form's error node. Scoped, because the other signup surface on the
 *   page has one too, and reading its message would attribute someone else's error to this form.
 * @param {(html: string) => { getElementById: (id: string) => any }} [parse]
 */
export function readOutcome(response, html, doneId, errorId, parse) {
  const parser = parse ?? ((source) => new DOMParser().parseFromString(source, 'text/html'));
  const parsed = parser(html);
  const done = parsed.getElementById(doneId);

  let posted = false;
  try {
    posted = new URL(response.url, window.location.href).searchParams.get('customer_posted') === 'true';
  } catch {
    posted = false;
  }

  if (done && posted) return { state: 'success', node: done, message: '' };

  /*
   * Shopify redirected, so the post landed — but the success panel is not where it was expected.
   * The address may well be subscribed, so this must not be reported as a failure, and it must not
   * be resubmitted. The caller follows the redirect as a GET and lets the server render the truth.
   */
  if (posted) return { state: 'posted-unconfirmed', node: null, message: '' };

  const error = parsed.getElementById(errorId);
  const message = error?.textContent?.trim() ?? '';
  if (message) return { state: 'error', node: null, message };

  return { state: 'unknown', node: null, message: '' };
}

/**
 * Enhanced submit: post in the background, then show whatever the server rendered.
 *
 * @param {SubmitEvent} event
 */
async function handleSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.classList.contains('timecard-newsletter-form')) return;

  const surface = form.closest('[data-timecard-newsletter-modal], .timecard-newsletter-band');
  const fromModal = Boolean(surface?.hasAttribute('data-timecard-newsletter-modal'));

  /*
   * Recorded before anything else, because the native path reloads the page and the reload has to
   * know which surface asked. Written on the fetch path too: an accepted post whose panel could
   * not be found is followed as a navigation, and that lands on a fresh page with no memory of
   * which surface was open.
   */
  store.write('session', STORAGE.pending, fromModal ? 'modal' : 'inline');

  /*
   * Captcha present: this is not ours to intercept, so the native submit proceeds untouched — no
   * preventDefault, no handler removed, no token bypassed.
   *
   * The button is still marked busy, but on a timeout so the marking happens after the browser has
   * taken the submission. Disabling a submit button synchronously inside its own submit event is
   * how a form quietly stops submitting.
   */
  if (/** @type {any} */ (window).Shopify?.captcha || form.querySelector('[data-shopify-captcha], .shopify-challenge__container')) {
    markBusyRecoverably(form);
    return;
  }

  if (!form.reportValidity()) {
    event.preventDefault();
    return;
  }

  event.preventDefault();

  const errorNode = form.querySelector('[data-timecard-newsletter-error]');
  const fields = form.querySelector('[data-timecard-newsletter-fields]');

  /*
   * Both ids are scoped to THIS form. The modal and the in-page band can be on the page together,
   * and reading the first matching node would let one surface adopt the other's success panel or
   * report the other's error.
   */
  const uid = errorNode?.id?.replace('TimecardNewsletterError-', '') ?? '';
  const targetDoneId = `TimecardNewsletterDone-${uid}`;
  const targetErrorId = `TimecardNewsletterError-${uid}`;

  const reveal = setBusy(form, true);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

  /** Hands the form back in a state the visitor can try again from. */
  const retryable = (/** @type {string} */ message) => {
    if (errorNode instanceof HTMLElement) {
      errorNode.textContent = message;
      errorNode.hidden = false;
    }
  };

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      signal: controller.signal,
    });

    const html = await response.text();
    const outcome = readOutcome(response, html, targetDoneId, targetErrorId);

    if (outcome.state === 'success' && outcome.node && fields instanceof HTMLElement) {
      /*
       * The server's own success markup, adopted whole. Rebuilding it here would mean keeping a
       * second copy of copy whose correctness depends on a store setting — and the copy that drifts
       * is the one telling someone they are subscribed when they are not.
       */
      fields.replaceWith(document.importNode(outcome.node, true));
      store.write('local', STORAGE.done, '1');
      store.remove('session', STORAGE.pending);
      syncLauncher();

      const adopted = form.querySelector('[data-timecard-newsletter-done]');
      if (adopted instanceof HTMLElement) adopted.focus();
      return;
    }

    if (outcome.state === 'error') {
      retryable(outcome.message);
      return;
    }

    if (outcome.state === 'posted-unconfirmed') {
      /*
       * Shopify accepted the post but the expected panel was not in the response. Following the
       * redirect as a navigation shows the server's own rendering of whatever really happened.
       *
       * Not a resubmission. This is a GET of the page Shopify redirected to — re-posting here
       * would send the same address a second time to settle a question the first post may already
       * have answered, and the theme has no way to know it had not.
       */
      window.location.assign(response.url);
      return;
    }

    /*
     * The answer could not be read at all.
     *
     * The form stays as it is, with its value, and says so. It is emphatically NOT resubmitted:
     * the first post may have succeeded, and a silent second one would be a duplicate mutation
     * made to resolve the theme's uncertainty rather than anything the visitor asked for.
     */
    retryable("We couldn't confirm that. Reload the page to check, or try again.");
    return;
  } catch (error) {
    /*
     * A network failure or a timeout. Nothing is claimed in either direction — the address may or
     * may not have reached Shopify — so the form comes back with its value intact and the visitor
     * decides whether to try again.
     */
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    retryable(
      aborted
        ? 'That took too long. Check your connection and try again.'
        : "We couldn't reach the server. Check your connection and try again."
    );
  } finally {
    window.clearTimeout(timeout);
    if (reveal) window.clearTimeout(reveal);
    setBusy(form, false);
  }
}

/* ─────────────────────────────────────────────────────── the auto-open timer ───── */

/**
 * Counts engaged time toward the auto-open, across pages.
 *
 * Stored as a running total in session storage rather than restarted per page, so a visitor who
 * browses four products for five seconds each is treated as the twenty-second visitor they are.
 * Time spent on a hidden tab does not count — it is not time spent with the store.
 *
 * @param {Element} modal
 */
function startAutoOpenTimer(modal) {
  const delayMs = Number(modal.getAttribute('data-delay') || 15) * 1000;
  const dismissDays = Number(modal.getAttribute('data-dismiss-days') || 1);

  if (modal.getAttribute('data-auto-open') === 'false') return;
  if (modal.hasAttribute('data-design-mode')) return;

  /*
   * No storage, no auto-open.
   *
   * Without persistence a dismissal cannot be remembered, and a popup that cannot remember being
   * dismissed is one that reappears on every page. The launcher still works, so the door is open —
   * it just stops knocking.
   */
  if (!store.available) return;

  if (hasSignedUp() || isDismissed(dismissDays)) return;

  let elapsed = Number(store.read('session', STORAGE.elapsed)) || 0;
  if (elapsed >= delayMs) elapsed = 0;

  /*
   * The state the CURRENT interval is being spent in, tracked here rather than read from the
   * document at the moment it is needed.
   *
   * `visibilitychange` fires AFTER `document.visibilityState` has already flipped, so a handler
   * that asks the document is asking about the interval that is starting, never the one that just
   * ended. That single confusion produced both halves of the bug: on the way to hidden the reading
   * was "hidden", so the seconds actually spent reading the page were thrown away; on the way back
   * to visible the reading was "visible", so the minutes spent in another tab were counted as
   * browsing. A visitor who tabbed away could come back to an instant popup, and one who never
   * left could browse indefinitely without the timer ever accumulating.
   */
  let visible = document.visibilityState === 'visible';
  let since = Date.now();
  let timer = 0;

  /*
   * One auto-open per page.
   *
   * The visibility listener re-arms the timer on every return to the tab. Without this the modal
   * could open, be dismissed, and be scheduled all over again by the next tab switch — the same
   * page asking twice.
   */
  let opened = false;

  /** Closes the interval that just ended, crediting it only if it was spent with the store. */
  const persist = () => {
    const now = Date.now();
    if (visible) elapsed += now - since;
    since = now;
    store.write('session', STORAGE.elapsed, String(elapsed));
  };

  const attempt = () => {
    persist();
    if (opened || hasSignedUp() || isDismissed(dismissDays)) return;

    if (openModal(modal, true)) {
      opened = true;
      store.write('session', STORAGE.elapsed, '0');
      return;
    }

    // Refused because the visitor is busy. Try again shortly rather than giving up for the session.
    timer = window.setTimeout(attempt, 5000);
  };

  timer = window.setTimeout(attempt, Math.max(delayMs - elapsed, 0));

  document.addEventListener('visibilitychange', () => {
    // Close the old interval first, while `visible` still describes it, and only then adopt the new.
    persist();
    visible = document.visibilityState === 'visible';

    window.clearTimeout(timer);
    if (visible && !opened) timer = window.setTimeout(attempt, Math.max(delayMs - elapsed, 0));
  });

  // Carry the accrued time to the next page rather than losing the last partial interval.
  window.addEventListener('pagehide', persist);
}

/* ───────────────────────────────────────────────────────────────────── wiring ───── */

document.addEventListener('submit', handleSubmit);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[data-timecard-newsletter-launcher]')) {
    // A click is an invitation, so nothing is guarded here except the stacking rule inside open().
    const modal = getModal();
    if (modal) whenDialogReady(modal).then(() => openModal(modal, false));
    return;
  }

  const closer = target.closest('[data-timecard-newsletter-close]');
  if (!closer) return;

  const modal = /** @type {any} */ (getModal());
  if (!modal) return;

  /*
   * Closing the success panel is not a dismissal. Someone who just signed up and clicked "Keep
   * exploring" has not refused anything, and recording it as a refusal would be the theme
   * mislabelling a completed transaction.
   */
  if (!modal.querySelector('[data-timecard-newsletter-done]')) {
    store.write('local', STORAGE.dismissed, String(Date.now()));
  }

  modal.closeDialog?.();
});

/**
 * Records a dismissal when the dialog is closed by Escape or by clicking outside.
 *
 * Both are handled inside Horizon's dialog component, so neither reaches the click handler above.
 * Without this, those two exits would leave no record and the modal would return on the next page.
 */
document.addEventListener(
  'dialog:close',
  (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.hasAttribute('data-timecard-newsletter-modal')) return;
    if (modal.querySelector('[data-timecard-newsletter-done]')) return;

    store.write('local', STORAGE.dismissed, String(Date.now()));
  },
  true
);

function init() {
  const modal = getModal();
  syncLauncher();
  if (!modal) return;

  /*
   * The native path landing back on the page.
   *
   * Shopify has reloaded us with the success state already rendered, but only the surface that was
   * actually submitted from should reopen — otherwise a footer signup would pop the modal open to
   * congratulate someone who never saw it.
   */
  const submittedFrom = store.read('session', STORAGE.pending);
  const done = modal.querySelector('[data-timecard-newsletter-done]');

  if (done) {
    store.write('local', STORAGE.done, '1');
    syncLauncher();

    if (submittedFrom === 'modal') {
      store.remove('session', STORAGE.pending);
      whenDialogReady(modal).then(() => {
        openModal(modal, false);
        if (done instanceof HTMLElement) done.focus();
      });
      return;
    }
  }

  if (document.querySelector('.timecard-newsletter-band [data-timecard-newsletter-done]')) {
    store.write('local', STORAGE.done, '1');
    store.remove('session', STORAGE.pending);
    syncLauncher();
  }

  /*
   * The timer waits for the dialog to be openable before it starts counting.
   *
   * Starting first would burn the delay against a modal that cannot open yet: `attempt` would find
   * `showDialog` missing, read that as "the visitor is busy", and fall into the five-second retry
   * loop — reaching the same place eventually, but by treating a load-order problem as a courtesy
   * one. The elapsed total is in session storage and is not lost by waiting.
   */
  whenDialogReady(modal).then(() => startAutoOpenTimer(modal));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

/**
 * The theme editor replaces sections wholesale, so anything read from the DOM has to be re-read.
 * Only the launcher's visibility depends on DOM that a section render can replace; the modal lives
 * in the layout and survives.
 */
document.addEventListener('shopify:section:load', syncLauncher);
